import { Avatar, Chip, ChipProps, Tooltip } from "@mui/material";
import { startCase } from "lodash";

import HomeIcon from '@mui/icons-material/Home';
import CameraIndoorIcon from '@mui/icons-material/CameraIndoor';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront'
import { LocalShift } from "~/hooks/visitDragDropReducer";
import { useMemo } from "react";

type Props = ChipProps & {
  providerTypeName: string;
  shifts?: LocalShift[] 
  initialsOnly?: boolean
  disableTooltip?: boolean
};

const ProviderTypeBG = [
  '#CFFAFE', // Original cyan
  '#FAE8FF', // Original purple
  '#EDE9FE', // Original indigo
  '#FEE2E2', // Original red
  '#E0F2FE', // Light blue
  '#F0FDF4', // Light green
  '#FEF3C7', // Light yellow
  '#FDF4FF', // Light pink
  '#EFF6FF', // Light blue
  '#F0FDFA', // Light teal
  '#FEFCE8', // Light yellow
  '#F5F3FF', // Light purple
  '#FEF2F2', // Light red
  '#ECFDF5', // Light emerald
  '#FDF2F8', // Light pink
  '#F0F9FF', // Light sky
  '#F5F3FF', // Light violet
  '#FEF9C3', // Light amber
  '#F0FDF4', // Light green
  '#FDF4FF'  // Light fuchsia
];

function getBackgroundColorIndex(name: string, length: number): number {
  let hash = 0;
  for (let i = 0; i < name?.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % length;
}

export default function ProviderTypeChip({ providerTypeName, shifts, sx, initialsOnly, disableTooltip = false, ...props }: Props) {
  const backgroundColor = ProviderTypeBG[getBackgroundColorIndex(providerTypeName, ProviderTypeBG.length)];
  const allInHome = useMemo(() => shifts?.every(shift => shift.providerTravel === "inHomeOnly"), [shifts]);
  const allVirtual = useMemo(() => shifts?.every(shift => shift.providerTravel === "virtualOnly"), [shifts]);
  const icon = useMemo(() => {
    if (!shifts?.length) return <></>;
    return allInHome ? <HomeIcon /> : (allVirtual ? <VideoCameraFrontIcon /> : <CameraIndoorIcon />);
  }, [shifts, allInHome, allVirtual]);
  
  const chipElement = (
    <Chip
      size="small"
      icon={icon}
      {...{
        ...(initialsOnly ? {
          avatar: <Avatar>{providerTypeName.charAt(0)}</Avatar>
        } : {})
      }}
      sx={{
        background: backgroundColor,
        color: "#000",
        fontSize: "11px",
        fontWeight: 500,
        minWidth: "60px",
        borderRadius: "40px",
        ...sx,
      }}
      label={initialsOnly ? null : startCase(providerTypeName)}
      {...props}
    />
  );

  return disableTooltip ? chipElement : (
    <Tooltip placement="top" title={allInHome ? "In-Home" : allVirtual ? "Virtual" : "In-Home or Virtual"}>
      {chipElement}
    </Tooltip>
  );
}
